/**
 * Client API typé. Gère l'auth (JWT en mémoire + refresh cookie httpOnly),
 * les refresh transparents, la gestion d'erreurs.
 */
// « same-origin » : l'API est relayée sous /api par le serveur qui sert le site, quelle que
// soit son adresse (app.ci-ville.ca, preprod.ci-ville.ca pendant une transition…)
const API_BASE = import.meta.env.VITE_API_URL === 'same-origin' ? '' : (import.meta.env.VITE_API_URL ?? 'http://localhost:3001');
const API_PREFIX = '/api/v1';
let accessToken = null;
/** Heure du dernier jeton reçu : le serveur ferme une session de navigateur non renouvelée depuis 30 min */
let accessTokenAt = 0;
let refreshPromise = null;
let onLogoutHandler = null;
let onPasswordChangeRequired = null;
export function setAccessToken(token) {
    accessToken = token;
    accessTokenAt = token ? Date.now() : 0;
}
export function getAccessTokenAge() {
    return accessTokenAt ? Date.now() - accessTokenAt : Infinity;
}
export function getAccessToken() {
    return accessToken;
}
export function onUnauthorized(handler) {
    onLogoutHandler = handler;
}
export function onPasswordChange(handler) {
    onPasswordChangeRequired = handler;
}
export class ApiError extends Error {
    status;
    code;
    details;
    constructor(status, code, message, details) {
        super(message);
        this.status = status;
        this.code = code;
        this.details = details;
    }
}
function toQuery(params) {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
        if (v === undefined || v === null || v === '')
            continue;
        if (Array.isArray(v)) {
            if (v.length)
                q.set(k, v.join(','));
        }
        else {
            q.set(k, String(v));
        }
    }
    const s = q.toString();
    return s ? `?${s}` : '';
}
/** Un seul rafraîchissement à la fois, même si plusieurs appels le demandent en parallèle. */
export async function refreshAccessToken() {
    if (refreshPromise)
        return refreshPromise;
    refreshPromise = (async () => {
        const res = await fetch(`${API_BASE}${API_PREFIX}/auth/refresh`, {
            method: 'POST',
            credentials: 'include',
        });
        if (!res.ok)
            throw new ApiError(res.status, 'REFRESH_FAILED', 'Session expirée');
        const data = await res.json();
        setAccessToken(data.accessToken);
        return data.accessToken;
    })().finally(() => {
        refreshPromise = null;
    });
    return refreshPromise;
}
async function send(method, path, body, options = {}) {
    const url = `${API_BASE}${API_PREFIX}${path}`;
    const headers = {};
    if (body !== undefined)
        headers['Content-Type'] = 'application/json';
    if (!options.skipAuth && accessToken)
        headers.Authorization = `Bearer ${accessToken}`;
    const res = await fetch(url, {
        method,
        headers,
        credentials: 'include',
        body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    if (res.status === 401 && !options.retry && !options.skipAuth) {
        try {
            await refreshAccessToken();
            return send(method, path, body, { ...options, retry: true });
        }
        catch {
            onLogoutHandler?.();
            throw new ApiError(401, 'SESSION_EXPIRED', 'Session expirée, veuillez vous reconnecter');
        }
    }
    if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        const message = Array.isArray(json.message) ? json.message.join(', ') : json.message;
        if (json.code === 'PASSWORD_CHANGE_REQUIRED' || json.code === 'TWO_FACTOR_SETUP_REQUIRED')
            onPasswordChangeRequired?.();
        throw new ApiError(res.status, json.code ?? 'UNKNOWN', message ?? 'Erreur inconnue', json.details);
    }
    return res;
}
async function request(method, path, body, options = {}) {
    const res = await send(method, path, body, options);
    if (res.status === 204)
        return undefined;
    return (await res.json().catch(() => undefined));
}
/** Télécharge un fichier servi par l'API (authentifié) et déclenche l'enregistrement. */
async function download(path, fallbackName) {
    const res = await send('GET', path);
    const disposition = res.headers.get('Content-Disposition') ?? '';
    const name = /filename="([^"]+)"/.exec(disposition)?.[1] ?? fallbackName;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
}
export const api = {
    // ── Auth ────────────────────────────────────────────────────────
    auth: {
        login: (input) => request('POST', '/auth/login', input, { skipAuth: true }),
        logout: () => request('POST', '/auth/logout'),
        me: () => request('GET', '/auth/me'),
        register: (input) => request('POST', '/auth/register', input, { skipAuth: true }),
        changePassword: (currentPassword, newPassword) => request('POST', '/auth/password/change', { currentPassword, newPassword }),
        forgotPassword: (email) => request('POST', '/auth/password/forgot', { email }, { skipAuth: true }),
        resetPassword: (token, newPassword) => request('POST', '/auth/password/reset', { token, newPassword }, { skipAuth: true }),
        verifyEmail: (token) => request('POST', '/auth/email/verify', { token }, { skipAuth: true }),
        setup2fa: () => request('POST', '/auth/2fa/setup'),
        enable2fa: (code) => request('POST', '/auth/2fa/enable', { code }),
        disable2fa: (password, code) => request('POST', '/auth/2fa/disable', { password, code }),
    },
    // ── Console GEOSIRA ─────────────────────────────────────────────
    admin: {
        overview: () => request('GET', '/admin/overview'),
        citizens: () => request('GET', '/admin/citizens'),
        usage: () => request('GET', '/admin/tenants/usage'),
        uncovered: () => request('GET', '/admin/territory/uncovered'),
        uncoveredReports: (params = {}) => request('GET', `/admin/territory/uncovered/reports${toQuery(params)}`),
        uncoveredReport: (id) => request('GET', `/admin/territory/uncovered/reports/${id}`),
        boundaryCandidates: (q) => request('GET', `/admin/territory/candidates${toQuery({ q })}`),
        setBoundary: (tenantId, geojson, source) => request('PUT', `/admin/tenants/${tenantId}/boundary`, { geojson, source }),
        billingSummary: () => request('GET', '/admin/billing/summary'),
        jobs: () => request('GET', '/admin/jobs'),
        runJob: (name) => request('POST', `/admin/jobs/${name}/run`),
        retryFailedMails: () => request('POST', '/admin/jobs/mail/retry-failed'),
        contracts: (tenantId) => request('GET', `/admin/tenants/${tenantId}/contracts`),
        createContract: (tenantId, input) => request('POST', `/admin/tenants/${tenantId}/contracts`, input),
        updateContract: (id, input) => request('PATCH', `/admin/contracts/${id}`, input),
        invoiceFromContract: (contractId) => request('POST', `/admin/contracts/${contractId}/invoice`),
        invoices: (params = {}) => request('GET', `/admin/invoices${toQuery(params)}`),
        invoice: (id) => request('GET', `/admin/invoices/${id}`),
        createInvoice: (tenantId, input) => request('POST', `/admin/tenants/${tenantId}/invoices`, input),
        updateInvoice: (id, input) => request('PATCH', `/admin/invoices/${id}`, input),
        issueInvoice: (id) => request('POST', `/admin/invoices/${id}/issue`),
        voidInvoice: (id) => request('POST', `/admin/invoices/${id}/void`),
        addPayment: (id, input) => request('POST', `/admin/invoices/${id}/payments`, input),
        offboardingDelete: (tenantId, confirmSlug, withoutExportReason) => request('POST', `/admin/tenants/${tenantId}/offboarding/delete`, { confirmSlug, withoutExportReason }),
        categories: () => request('GET', '/categories/admin'),
        createCategory: (input) => request('POST', '/categories', input),
        updateCategory: (id, input) => request('PATCH', `/categories/${id}`, input),
        createSubcategory: (categoryId, input) => request('POST', `/categories/${categoryId}/subcategories`, input),
        updateSubcategory: (id, input) => request('PATCH', `/categories/subcategories/${id}`, input),
        createRedirect: (input) => request('POST', '/categories/redirects', input),
        updateRedirect: (id, input) => request('PATCH', `/categories/redirects/${id}`, input),
    },
    // ── Accès support ───────────────────────────────────────────────
    support: {
        list: (tenantId) => request('GET', `/tenants/${tenantId}/support-access`),
        active: (tenantId) => request('GET', `/tenants/${tenantId}/support-access/active`),
        request: (tenantId, reason, durationHours) => request('POST', `/tenants/${tenantId}/support-access`, { reason, durationHours }),
        emergency: (tenantId, reason) => request('POST', `/tenants/${tenantId}/support-access/emergency`, { reason }),
        approve: (grantId) => request('POST', `/support-access/${grantId}/approve`),
        deny: (grantId) => request('POST', `/support-access/${grantId}/deny`),
        revoke: (grantId) => request('POST', `/support-access/${grantId}/revoke`),
    },
    // ── Journal d'audit ─────────────────────────────────────────────
    audit: {
        list: (params) => request('GET', `/audit${toQuery(params)}`),
        download: (params) => download(`/audit/export${toQuery(params)}`, 'journal-audit.csv'),
    },
    // ── Users ───────────────────────────────────────────────────────
    users: {
        me: () => request('GET', '/users/me'),
        tenantMembers: (tenantId) => request('GET', `/users/tenant/${tenantId}/members`),
        assignable: (tenantId) => request('GET', `/users/tenant/${tenantId}/assignable`),
        updateMember: (tenantId, userId, input) => request('PATCH', `/users/tenant/${tenantId}/members/${userId}`, input),
        removeMember: (tenantId, userId) => request('DELETE', `/users/tenant/${tenantId}/members/${userId}`),
        reset2fa: (tenantId, userId) => request('POST', `/users/tenant/${tenantId}/members/${userId}/reset-2fa`),
    },
    // ── Tenants ─────────────────────────────────────────────────────
    zones: {
        list: (tenantId, geometry = false) => request('GET', `/tenants/${tenantId}/zones${geometry ? '?geometry=true' : ''}`),
        import: (tenantId, input) => request('POST', `/tenants/${tenantId}/zones/import`, input),
        removeAll: (tenantId) => request('DELETE', `/tenants/${tenantId}/zones`),
    },
    poi: {
        list: (tenantId) => request('GET', `/tenants/${tenantId}/poi`),
        create: (tenantId, input) => request('POST', `/tenants/${tenantId}/poi`, input),
        update: (tenantId, poiId, input) => request('PATCH', `/tenants/${tenantId}/poi/${poiId}`, input),
        remove: (tenantId, poiId) => request('DELETE', `/tenants/${tenantId}/poi/${poiId}`),
    },
    tenants: {
        list: () => request('GET', '/tenants'),
        get: (id) => request('GET', `/tenants/${id}`),
        getBySlug: (slug) => request('GET', `/tenants/by-slug/${slug}`, undefined, { skipAuth: true }),
        create: (input) => request('POST', '/tenants', input),
        adminUpdate: (id, input) => request('PATCH', `/tenants/${id}`, input),
        updateBranding: (id, input) => request('PATCH', `/tenants/${id}/branding`, input),
        subscription: (id) => request('GET', `/tenants/${id}/subscription`),
        invoice: (id, invoiceId) => request('GET', `/tenants/${id}/invoices/${invoiceId}`),
        boundary: (id) => request('GET', `/tenants/${id}/boundary`),
        downloadExport: (id) => download(`/tenants/${id}/offboarding/export`, 'export-ci-ville.zip'),
        archive: (id) => request('DELETE', `/tenants/${id}`),
        listAdmins: (id) => request('GET', `/tenants/${id}/admins`),
        provisionAdmin: (id, input) => request('POST', `/tenants/${id}/admins`, input),
    },
    // ── Categories (catalogue universel) ────────────────────────────
    categories: {
        /** Catalogue universel actif (tous les éléments, avec leur activation par défaut) */
        list: () => request('GET', '/categories', undefined, { skipAuth: true }),
        /** Ce qu'une ville (ou un lieu) offre, avec les redirections applicables */
        offered: (params) => request('GET', `/categories/offered${toQuery(params)}`, undefined, { skipAuth: true }),
        tenant: (tenantId) => request('GET', `/tenants/${tenantId}/catalog`),
        setTenant: (tenantId, input) => request('PUT', `/tenants/${tenantId}/catalog`, input),
    },
    // ── Reports ─────────────────────────────────────────────────────
    reports: {
        list: (params) => request('GET', `/reports${toQuery(params)}`),
        map: (params) => request('GET', `/reports/map${toQuery(params)}`),
        stats: (params) => request('GET', `/reports/stats${toQuery(params)}`),
        hotspots: (params) => request('GET', `/reports/analysis/hotspots${toQuery(params)}`),
        roadSegments: (params) => request('GET', `/reports/analysis/road-segments${toQuery(params)}`),
        recurrent: (params) => request('GET', `/reports/analysis/recurrent${toQuery(params)}`),
        exportJson: (params) => request('GET', `/reports/export${toQuery({ ...params, format: 'json' })}`),
        download: (params, format) => download(`/reports/export${toQuery({ ...params, format })}`, `signalements.${format}`),
        get: (id) => request('GET', `/reports/${id}`),
        create: (input) => request('POST', '/reports', input),
        update: (id, input) => request('PATCH', `/reports/${id}`, input),
        remove: (id) => request('DELETE', `/reports/${id}`),
        revealContact: (id, reason) => request('POST', `/reports/${id}/contact`, { reason }),
        citizenSearch: (tenantId, email, reason) => request('GET', `/reports/citizen-search${toQuery({ tenantId, email, reason })}`),
    },
    // ── Portail citoyen (public, sans compte) ───────────────────────
    public: {
        lookup: (reference, email) => request('POST', '/public/track', { reference, email }, { skipAuth: true }),
        track: (ref, t) => request('GET', `/public/track${toQuery({ ref, t })}`, undefined, { skipAuth: true }),
        feedback: (ref, t, input) => request('POST', '/public/feedback', { ref, t, ...input }, { skipAuth: true }),
        city: (slug) => request('GET', `/public/cities/${slug}`, undefined, { skipAuth: true }),
        cityReports: (slug, params) => request('GET', `/public/cities/${slug}/reports${toQuery(params)}`, undefined, { skipAuth: true }),
    },
    // ── Uploads (formulaire signé, bucket privé) ────────────────────
    uploads: {
        presign: (contentType) => request('POST', '/uploads/presign', { contentType }),
    },
    // ── Géographie (proxy serveur) ──────────────────────────────────
    geo: {
        search: (q, tenantId) => request('GET', `/geo/search${toQuery({ q, tenantId })}`),
        reverse: (lat, lng) => request('GET', `/geo/reverse${toQuery({ lat, lng })}`),
        route: (points, optimize) => request('GET', `/geo/route${toQuery({ points: points.map((p) => `${p.longitude},${p.latitude}`).join(';'), optimize })}`),
    },
    // ── Comments ────────────────────────────────────────────────────
    comments: {
        list: (reportId) => request('GET', `/reports/${reportId}/comments`),
        create: (reportId, body, isInternal) => request('POST', `/reports/${reportId}/comments`, { body, isInternal }),
        remove: (reportId, commentId) => request('DELETE', `/reports/${reportId}/comments/${commentId}`),
    },
    // ── Notifications ───────────────────────────────────────────────
    notifications: {
        list: () => request('GET', '/notifications'),
        unreadCount: () => request('GET', '/notifications/unread-count'),
        markRead: (id) => request('POST', `/notifications/${id}/read`),
        markAllRead: () => request('POST', '/notifications/read-all'),
    },
    // ── Invitations ─────────────────────────────────────────────────
    invitations: {
        invite: (tenantId, email, role) => request('POST', `/tenants/${tenantId}/invitations`, { email, role }),
        list: (tenantId) => request('GET', `/tenants/${tenantId}/invitations`),
        revoke: (id) => request('DELETE', `/invitations/${id}`),
        accept: (token, firstName, lastName, password) => request('POST', '/invitations/accept', { token, firstName, lastName, password }, { skipAuth: true }),
    },
};
//# sourceMappingURL=api-client.js.map